import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import LogoutButton from './LogoutButton';
import UserDisplay from './UserDisplay';
import { useSwipeable } from 'react-swipeable';
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "./components/ui/card";

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// -----------------------
// Funciones de encriptación
// -----------------------
const getEncryptionKey = async (userId) => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(userId),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('your-salt-here'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

const encryptNote = async (text, key) => {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(text)
  );
  return {
    encrypted: Array.from(new Uint8Array(encrypted)),
    iv: Array.from(iv)
  };
};

const decryptNote = async (encryptedData, key) => {
  try {
    const decoder = new TextDecoder();
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(encryptedData.iv) },
      key,
      new Uint8Array(encryptedData.encrypted)
    );
    return decoder.decode(decrypted);
  } catch (error) {
    // Si falla la desencriptación, se asume que la nota está sin encriptar
    return encryptedData;
  }
};

// -----------------------
// Componente EnhancedCalendar
// -----------------------
const EnhancedCalendar = ({ user, setShowLogin }) => {
  // Estados
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [notes, setNotes] = useState({});
  const [currentNote, setCurrentNote] = useState('');
  const [error, setError] = useState(null);
  const [holidays, setHolidays] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [slideDirection, setSlideDirection] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);

  // -----------------------
  // useEffect para obtener ubicación y notas
  // -----------------------
  useEffect(() => {
    getUserLocation();
    if (user) {
      fetchNotes(user);
    } else {
      setNotes({});
    }
  }, [user]);

  useEffect(() => {
    if (userLocation) {
      fetchHolidays();
    }
  }, [userLocation, currentDate]);

  // -----------------------
  // Funciones auxiliares
  // -----------------------

  // Obtiene la ubicación del usuario
  const getUserLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
          setUserLocation(null);
        }
      );
    } else {
      console.error('Geolocation is not available in this browser.');
      setUserLocation(null);
    }
  };

  // Obtiene los días festivos según la ubicación y el mes actual
  const fetchHolidays = async () => {
    if (!userLocation) {
      setHolidays({});
      return;
    }
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    try {
      const geoResponse = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}`
      );
      const geoData = await geoResponse.json();
      const countryCode = geoData.countryCode;

      const response = await fetch(
        `https://openholidaysapi.org/PublicHolidays?countryIsoCode=${countryCode}&languageIsoCode=EN&validFrom=${year}-${month
          .toString()
          .padStart(2, '0')}-01&validTo=${year}-${month.toString().padStart(2, '0')}-${lastDay}&latitude=${userLocation.latitude}&longitude=${userLocation.longitude}`
      );
      const data = await response.json();

      const holidayMap = (data || [])
        .filter((holiday) => holiday.nationwide || holiday.regionalScope === 'Regional')
        .reduce((acc, holiday) => {
          const date = new Date(holiday.startDate);
          const dateString = formatDate(date);
          const holidayName = holiday.name.find((n) => n.language === 'EN')?.text || holiday.name[0].text;
          acc[dateString] = {
            name: holidayName,
            scope: holiday.nationwide ? 'National' : holiday.regionalScope,
            region: holiday.subdivisions?.[0]?.shortName || ''
          };
          return acc;
        }, {});

      setHolidays(holidayMap);
      console.log('Fetched holidays:', holidayMap);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setError('Failed to fetch holidays. Please try again.');
    }
  };

  // Obtiene las notas del usuario y trata de desencriptarlas
  const fetchNotes = async (user) => {
    try {
      // Supabase/PostgREST caps each response at ~1000 rows. Once a user has
      // more than 1000 notes, an unpaginated select silently drops the newest
      // ones, so freshly-saved notes "disappear" after a reload while older
      // notes still show. Page through every row so all notes load regardless
      // of the total count.
      const pageSize = 1000;
      let allNotes = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('user_id', user.id)
          .order('id', { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;

        allNotes = allNotes.concat(data || []);
        if (!data || data.length < pageSize) break;
        from += pageSize;
      }

      const key = await getEncryptionKey(user.id);
      const notesObject = {};

      for (const note of allNotes) {
        let content;
        try {
          const parsedContent = JSON.parse(note.content);
          content = await decryptNote(parsedContent, key);
        } catch (e) {
          content = note.content;
        }

        if (!notesObject[note.date]) {
          notesObject[note.date] = [];
        }
        notesObject[note.date].push({
          id: note.id,
          content,
          completed: note.completed
        });
      }

      setNotes(notesObject);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Failed to fetch notes. Please try again.');
    }
  };

  // Formatea una fecha al formato "yyyy-mm-dd"
  const formatDate = (date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0];
  };

  // Maneja el click sobre una fecha del calendario
  const handleDateClick = useCallback((date) => {
    setSelectedDate(date);
    setCurrentNote('');
    setEditingNoteId(null);
    setError(null);
  }, []);

  // Maneja eventos touch para iOS - proporciona feedback inmediato
  const handleTouchStart = useCallback((date, event) => {
    // Previene comportamientos de touch por defecto en iOS
    event.preventDefault();
    // Fuerza una actualización inmediata del estado
    setSelectedDate(date);
    setCurrentNote('');
    setEditingNoteId(null);
    setError(null);
  }, []);

  // Force immediate re-render on iOS when selectedDate changes
  useEffect(() => {
    // This effect ensures the component re-renders immediately when selectedDate changes
    // which helps with the iOS Chrome delay issue
  }, [selectedDate]);

  // Agrega o actualiza una nota
  const handleAddNote = async () => {
    if (selectedDate && currentNote.trim() !== '' && user) {
      const dateKey = formatDate(selectedDate);
      try {
        const key = await getEncryptionKey(user.id);
        const encryptedData = await encryptNote(currentNote, key);
        const encryptedContent = JSON.stringify(encryptedData);

        if (editingNoteId) {
          // Update existing note
          const { error } = await supabase
            .from('notes')
            .update({
              content: encryptedContent,
              // We don't update date or completed status here
            })
            .eq('id', editingNoteId)
            .eq('user_id', user.id);

          if (error) throw error;

          setNotes((prevNotes) => {
            const updatedNotes = { ...prevNotes };
            updatedNotes[dateKey] = updatedNotes[dateKey].map((note) =>
              note.id === editingNoteId ? { ...note, content: currentNote } : note
            );
            return updatedNotes;
          });
          setEditingNoteId(null);
        } else {
          // Insert new note
          const { data, error } = await supabase
            .from('notes')
            .insert({
              user_id: user.id,
              date: dateKey,
              content: encryptedContent,
              completed: false
            })
            .select();

          if (error) throw error;

          setNotes((prevNotes) => ({
            ...prevNotes,
            [dateKey]: [
              ...(prevNotes[dateKey] || []),
              { id: data[0].id, content: currentNote, completed: false }
            ]
          }));
        }
        setCurrentNote('');
        setError(null);
      } catch (error) {
        console.error('Error saving note:', error);
        setError('Failed to save note. Please try again.');
      }
    }
  };

  const handleStartEdit = (note) => {
    setCurrentNote(note.content);
    setEditingNoteId(note.id);
    setError(null);
  };

  const handleCancelEdit = () => {
    setCurrentNote('');
    setEditingNoteId(null);
    setError(null);
  };

  // Permite agregar la nota al presionar Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNote();
    }
  };

  // Elimina una nota
  const handleDeleteNote = async (dateKey, noteId) => {
    if (user) {
      try {
        const { error } = await supabase
          .from('notes')
          .delete()
          .eq('id', noteId)
          .eq('user_id', user.id);

        if (error) throw error;

        setNotes((prevNotes) => {
          const updatedNotes = { ...prevNotes };
          updatedNotes[dateKey] = updatedNotes[dateKey].filter((note) => note.id !== noteId);
          if (updatedNotes[dateKey].length === 0) {
            delete updatedNotes[dateKey];
          }
          return updatedNotes;
        });
        setError(null);
      } catch (error) {
        console.error('Error deleting note:', error);
        setError('Failed to delete note. Please try again.');
      }
    }
  };

  // Alterna el estado "completed" de una nota
  const handleToggleNoteCompleted = async (dateKey, noteId, currentCompleted) => {
    if (user) {
      try {
        const { error } = await supabase
          .from('notes')
          .update({ completed: !currentCompleted })
          .eq('id', noteId)
          .eq('user_id', user.id);

        if (error) throw error;

        setNotes((prevNotes) => {
          const updatedNotes = { ...prevNotes };
          updatedNotes[dateKey] = updatedNotes[dateKey].map((note) =>
            note.id === noteId ? { ...note, completed: !currentCompleted } : note
          );
          return updatedNotes;
        });
      } catch (error) {
        console.error('Error toggling note completion:', error);
        setError('Failed to update note completion. Please try again.');
      }
    }
  };

  // Maneja el logout del usuario
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };

  // Cambia de mes con animación hacia la derecha
  const handlePrevMonth = () => {
    setSlideDirection('right');
    setTimeout(() => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      setSlideDirection(null);
    }, 300);
  };

  // Cambia de mes con animación hacia la izquierda
  const handleNextMonth = () => {
    setSlideDirection('left');
    setTimeout(() => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      setSlideDirection(null);
    }, 300);
  };

  // Configuración del swipe para que se comporte igual que las flechas
  const handlers = useSwipeable({
    onSwipedLeft: handleNextMonth,
    onSwipedRight: handlePrevMonth,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  // Calcula los días a mostrar en el calendario (incluye días de meses adyacentes)
  const getDaysArray = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    let firstDay = new Date(year, month, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1; // Ajusta para que la semana inicie en lunes
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days = [];

    // Días del mes anterior
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    // Días del mes siguiente
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    return days;
  };

  const today = new Date();
  const daysArray = getDaysArray();

  return (
    <Card className="w-full max-w-md mx-auto shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        {user && <UserDisplay user={user} />}
        {user && <LogoutButton onLogout={handleLogout} />}
      </CardHeader>

      <CardContent>
        {/* Navegación entre meses */}
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>

          {/* Month and Year Selectors - Keeping native select for now for simplicity but styled */}
          <div className="flex items-center gap-2">
            <select
              value={currentDate.getMonth()}
              onChange={(e) => setCurrentDate(new Date(currentDate.getFullYear(), parseInt(e.target.value), 1))}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>
                  {new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>

            <select
              value={currentDate.getFullYear()}
              onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), currentDate.getMonth(), 1))}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {Array.from({ length: 21 }, (_, i) => {
                const year = new Date().getFullYear() - 10 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
        </div>

        {/* Calendario: grid de días con swipe */}
        <div
          {...handlers}
          className={`grid grid-cols-7 gap-1 mb-4 transition-all duration-300 ${slideDirection === 'left'
            ? 'translate-x-full opacity-0'
            : slideDirection === 'right'
              ? '-translate-x-full opacity-0'
              : 'translate-x-0 opacity-100'
            }`}
        >
          {daysOfWeek.map((day) => (
            <div key={day} className="text-center font-light text-gray-500">
              {day}
            </div>
          ))}
          {daysArray.map(({ date, isCurrentMonth }) => {
            const dateKey = formatDate(date);
            const isSelected = selectedDate && formatDate(selectedDate) === dateKey;
            const hasNotes = notes[dateKey] && notes[dateKey].length > 0;
            const isToday =
              date.getDate() === today.getDate() &&
              date.getMonth() === today.getMonth() &&
              date.getFullYear() === today.getFullYear();
            const holiday = holidays[dateKey];
            return (
              <button
                key={dateKey}
                onClick={() => handleDateClick(date)}
                onTouchStart={(event) => handleTouchStart(date, event)}
                className={`text-center p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-150
                ${isSelected ? 'bg-gray-200 calendar-day-selected' : ''}
                ${hasNotes ? 'font-bold' : ''}
                ${isToday ? 'bg-gray-100 ring-2 ring-gray-300' : ''}
                ${!isCurrentMonth ? 'text-gray-300' : ''}
                ${holiday ? 'text-red-500 font-bold' : ''}
                active:bg-gray-300
              `}
                title={
                  holiday
                    ? `${holiday.name} (${holiday.scope}${holiday.region ? ` - ${holiday.region}` : ''
                    })`
                    : ''
                }
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {/* Sección para agregar y listar notas del día seleccionado */}
        {selectedDate && user && (
          <div className="mt-8 border-t pt-4">
            <h3 className="text-lg font-medium mb-4 flex items-center">
              {selectedDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              {holidays[formatDate(selectedDate)] && (
                <span className="ml-2 text-red-500 font-normal">
                  {holidays[formatDate(selectedDate)].name}
                  <span className="text-sm ml-2 text-muted-foreground">
                    (
                    {holidays[formatDate(selectedDate)].scope}
                    {holidays[formatDate(selectedDate)].region &&
                      ` - ${holidays[formatDate(selectedDate)].region}`}
                    )
                  </span>
                </span>
              )}
            </h3>
            {/* Formulario para agregar nota */}
            <div className="flex gap-2 mb-4">
              <Input
                type="text"
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter a note..."
                className="flex-grow"
              />
              <Button onClick={handleAddNote}>
                {editingNoteId ? 'Update' : 'Add'}
              </Button>
              {editingNoteId && (
                <Button
                  variant="destructive"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              )}
            </div>
            {error && <p className="text-destructive text-sm mb-2">{error}</p>}
            {/* Listado de notas */}
            <ul className="space-y-2">
              {notes[formatDate(selectedDate)]?.map((note) => (
                <li key={note.id} className="flex items-center bg-secondary/50 p-2 rounded-md">
                  <div className="flex items-center gap-2 flex-grow">
                    <input
                      type="checkbox"
                      id={`note-${note.id}`}
                      checked={note.completed}
                      onChange={() =>
                        handleToggleNoteCompleted(
                          formatDate(selectedDate),
                          note.id,
                          note.completed
                        )
                      }
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor={`note-${note.id}`}
                      className={`text-sm ${note.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}
                    >
                      {note.content}
                    </label>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleStartEdit(note)}
                    >
                      ✎
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteNote(formatDate(selectedDate), note.id)}
                    >
                      ×
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Botón de login si no hay usuario */}
        {!user && (
          <CardFooter className="justify-center pt-8">
            <Button
              onClick={() => setShowLogin(true)}
              size="lg"
              className="w-full"
            >
              Wanna take notes? Log in
            </Button>
          </CardFooter>
        )}
      </CardContent>
    </Card>
  );
};


export default EnhancedCalendar;
